import React, { useState, useEffect } from 'react';
import '../styles/flipCard.css';
import { openGoogleCalendar, downloadICal } from '../utils/calendarExport';
import { formatRelativeDayLabel } from '../utils/relativeDay';

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const GoogleMark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const AppleMark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.37 12.29c.03 3.03 2.66 4.04 2.69 4.05-.02.07-.42 1.44-1.39 2.85-.84 1.22-1.71 2.43-3.08 2.46-1.35.03-1.78-.8-3.33-.8-1.54 0-2.01.78-3.29.83-1.32.05-2.33-1.32-3.18-2.53C3.07 16.4 1.9 12.7 3.7 10.2c.89-1.24 2.49-2.02 4.22-2.05 1.32-.03 2.56.89 3.33.89.77 0 2.23-1.1 3.76-.94.64.03 2.44.26 3.6 1.95-.09.06-2.15 1.26-2.24 3.24zM14.7 6.54c.71-.86 1.2-2.06 1.07-3.26-1.03.04-2.28.69-3.02 1.55-.66.76-1.24 1.98-1.08 3.15 1.14.09 2.31-.58 3.03-1.44z" />
  </svg>
);

const EventCardBack = ({
  event,
  subscriptionData,
  onSubscribe,
  onCancel,
  allowPush = true,
}) => {
  const [channels, setChannels] = useState(subscriptionData?.channels || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    setChannels(subscriptionData?.channels || []);
  }, [subscriptionData]);

  const togglePush = async () => {
    setError(null);
    const previous = channels;
    const next = channels.includes('web')
      ? channels.filter((c) => c !== 'web')
      : [...channels.filter((c) => c !== 'email'), 'web'];

    setChannels(next);

    try {
      setLoading(true);
      await onSubscribe(next, false);
    } catch (err) {
      console.error('Error toggling push:', err);
      setChannels(previous);
      setError(err.message || 'Failed to update subscription. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const webOn = channels.includes('web');
  const hasCalendar = Boolean(event.start || event.startsAt);

  return (
    <div className="event-card-back">
      <div className="notify-meta">
        <div className="event-title-back">{event.title}</div>
        {event.start && (
          <>
            <div className="event-time-back">
              {formatRelativeDayLabel(event.start, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </div>
            {event.end && (
              <div className="event-timerange-back">
                {new Date(event.start).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {' – '}
                {new Date(event.end).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="notify-actions" role="group" aria-label="Reminder options">
          {allowPush ? (
          <button
            type="button"
            className={`notify-row${webOn ? ' is-on' : ''}`}
            onClick={togglePush}
            disabled={loading}
            aria-pressed={webOn}
          >
            <span className="notify-row-icon"><BellIcon /></span>
            <span className="notify-row-label">
              <strong>Push</strong>
              <span>Browser notifications</span>
            </span>
            <span
              className={`notify-toggle${webOn ? ' is-on' : ''}`}
              aria-hidden="true"
            />
          </button>
          ) : null}

          {hasCalendar && (
            <div className={`calendar-bubble${calendarOpen ? ' is-split' : ''}`}>
              {!calendarOpen ? (
                <button
                  type="button"
                  className="calendar-pill"
                  onClick={() => setCalendarOpen(true)}
                  aria-expanded={false}
                >
                  <span className="notify-row-icon"><CalendarIcon /></span>
                  <span className="notify-row-label">
                    <strong>Calendar</strong>
                    <span>Add to your calendar</span>
                  </span>
                </button>
              ) : (
                <div className="calendar-split" role="menu" aria-label="Choose calendar">
                  <button
                    type="button"
                    role="menuitem"
                    className="calendar-half calendar-half--google"
                    onClick={() => openGoogleCalendar(event)}
                  >
                    <GoogleMark />
                    <span>Google</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="calendar-half calendar-half--ios"
                    onClick={() => downloadICal(event)}
                  >
                    <AppleMark />
                    <span>iOS</span>
                  </button>
                </div>
              )}
            </div>
          )}
      </div>

      <div className="notify-footer">
        {error && <p className="error-message">{error}</p>}
        <button type="button" className="back-flip-btn" onClick={onCancel} aria-label="Back to event">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default EventCardBack;
